import { ReflagProvider, useClient } from '@reflag/react-sdk';
import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import * as React from 'react';
import { RecoilRoot } from 'recoil';

import { withReflag } from '@gitbook/adaptive';
import * as analytics from '@gitbook/app-analytics/client';
import { isGitBookStaff } from '@gitbook/app-database';
import type { EnabledFeatureFlagsMap } from '@gitbook/app-featureflags';
import type { NavigationScreenOrgSettings, NavigationScreenState } from '@gitbook/app-navigation';
import { env, webConfig } from '@gitbook/env';
import { IconsProvider } from '@gitbook/icons';
import { UIKitProvider } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ConfirmProvider,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
    GitBookLoader,
    ImageProvider,
    RouterProvider,
    Toaster,
    toast,
} from '@gitbook/spine-react';

import { AppLayout, OfflineOverlay } from './AppLayout';
import {
    AgentProvider,
    AppSearch,
    DndProvider,
    ErrorScreen,
    HiveAppProvider,
    ImportRunToasts,
    MediaGallery,
    NotificationsProvider,
    OrganizationAccessPlaceholder,
    useHideSplashscreen,
    useNotificationToasts,
} from './Components';
import {
    HistoryProvider,
    type NavigateOptions,
    NavigationProvider,
    NavigationRedirect,
    NavigationScreenRenderer,
    type NavigationTarget,
    useNavigationFunctions,
    useNavigationState,
} from './navigation';
import {
    AgentsScreen,
    ChangeRequestsScreen,
    ChangeSitePlanDialog,
    CollectionScreen,
    ContentKitBuilderScreen,
    EnforceSSOWrapper,
    HomeScreen,
    ImportScreen,
    IntegrationScreen,
    IntegrationsScreen,
    InvitesOrganizationScreen,
    JoinOrganizationScreen,
    LoginScreenWrapper,
    OnboardingFlow,
    OpenAPISpecScreen,
    OpenAPISpecsHomeScreen,
    OrganizationBillingSettings,
    OrganizationMigrateSettings,
    RedirectOrgWithBasename,
    RootRedirect,
    SettingsScreen,
    SiteScreen,
    SitesListScreen,
    SiteUpgradeModal,
    SpaceScreen,
    TranslationsScreen,
    TrashScreen,
} from './Screens';
import { ContentSidebar, SettingsSidebar } from './Sidebars';
import {
    apiQuery,
    apiQueryClient,
    apiQueryPersistOptions,
    autoStartLogSession,
    CurrentOrganizationProvider,
    FirebaseAuthProvider,
    UserDataProvider,
    useCurrentOrganizationId,
    useCustomerIoInAppMessages,
    useLoggedInUser,
    usePersistOnboardingParamsFromURL,
    useUpdateOrgMemberLastSeenAt,
} from './state';
import { getImageResizedURL, useDevelopmentLiveReload } from './utils';

autoStartLogSession();

/**
 * Entry point component.
 */
export function Application() {
    // Capture and persist Onboarding params from the URL, if present
    usePersistOnboardingParamsFromURL();

    // Worst case scenario, we always hide the splashscreen after 3 seconds
    // to ensure it doesn't get stuck on an unhandled route
    useHideSplashscreen(4000, true);
    useDevelopmentLiveReload();

    return (
        <ImageProvider getResizedURL={getImageResizedURL}>
            <PersistQueryClientProvider
                client={apiQueryClient}
                persistOptions={apiQueryPersistOptions}
            >
                <RecoilRoot>
                    <ErrorScreen>
                        <IconsProvider
                            assetsURL="https://ka-p.fontawesome.com/releases/v6.6.0/"
                            assetsURLToken={webConfig.icons.token}
                            assetsByStyles={{
                                'custom-icons': {
                                    assetsURL: '/public/icons/',
                                },
                            }}
                        >
                            <ConfirmProvider>
                                <UIKitProvider>
                                    <DndProvider>
                                        <FirebaseAuthProvider>
                                            <HiveAppProvider>
                                                <React.Suspense
                                                    fallback={<FullScreenLoadingFallback />}
                                                >
                                                    <HistoryProvider>
                                                        <NavigationProvider persist>
                                                            <ScreenNavigationRouterProvider>
                                                                <LoginScreenWrapper>
                                                                    <UserDataProvider>
                                                                        <ApplicationWithOrganization>
                                                                            <AgentProvider>
                                                                                <MainApplication />
                                                                            </AgentProvider>
                                                                        </ApplicationWithOrganization>
                                                                    </UserDataProvider>
                                                                </LoginScreenWrapper>
                                                                <Toaster />
                                                            </ScreenNavigationRouterProvider>
                                                        </NavigationProvider>
                                                    </HistoryProvider>
                                                    <OfflineOverlay />
                                                </React.Suspense>
                                            </HiveAppProvider>
                                        </FirebaseAuthProvider>
                                    </DndProvider>
                                </UIKitProvider>
                            </ConfirmProvider>
                        </IconsProvider>
                    </ErrorScreen>
                </RecoilRoot>
            </PersistQueryClientProvider>
        </ImageProvider>
    );
}

export type Href = NavigationTarget | string;

declare module '@gitbook/spine-react' {
    interface RouterConfig {
        href: Href;
        routerOptions: NavigateOptions;
    }
}

/**
 * Provider to navigate to a screen when a link is clicked in Spine components.
 */
function ScreenNavigationRouterProvider(props: { children: React.ReactNode }) {
    const { navigate, getNavigationURL } = useNavigationFunctions();
    return (
        <RouterProvider
            navigate={(href, options) => {
                if (typeof href === 'string') {
                    window.location.href = href;
                } else {
                    navigate(href, options);
                }
            }}
            useHref={(href: Href) => {
                if (typeof href === 'string') {
                    return href;
                }
                return getNavigationURL(href);
            }}
        >
            {props.children}
        </RouterProvider>
    );
}

/**
 * Application component being rendered when an user is logged in.
 */
const MainApplication = React.memo(function MainApplication() {
    useCustomerIoInAppMessages();
    useNotificationToasts();
    const currentUser = useLoggedInUser();
    const { screen } = useNavigationState();

    return (
        <ReflagProvider
            publishableKey={webConfig.bucketCo.publishableKey}
            user={{
                id: currentUser.uid,
                email: currentUser.email ?? undefined,
                name: currentUser.displayName ?? '',
            }}
            company={
                'organization' in screen && screen.organization
                    ? {
                          id: screen.organization,
                      }
                    : undefined
            }
            toolbar={
                (isGitBookStaff(currentUser) || env === 'development') &&
                !process.env.GITBOOK_PREDICTABLE_UI
            }
        >
            <TrackFeatureFlags />
            <NotificationsProvider>
                <div className="flex min-h-0 flex-1 flex-col bg-side">
                    <NavigationScreenRenderer
                        Screen={ScreenRouter}
                        fallback={<FullScreenLoadingFallback />}
                    />
                    <AppSearch />
                </div>
                <MediaGallery />
                <SiteUpgradeModal />
                <ChangeSitePlanDialog />
                <ImportRunToasts />
            </NotificationsProvider>
        </ReflagProvider>
    );
});

function ApplicationWithOrganization({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    // Check if the user has access to the current organization
    const { screen } = useNavigationState();
    let orgKey: string | undefined;
    if (screen.type === 'space' || screen.type === 'collection') {
        orgKey = screen.organization;
    }

    const [
        { data: organization },
        {
            data: { items: userOrganizations },
        },
    ] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', orgKey ? { organizationId: orgKey } : skipToken),
            apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }),
        ],
    });

    const currentOrgId =
        screen.type !== 'org_join' && 'organization' in screen ? screen.organization : null;
    const userIsMemberOfCurrentOrg = currentOrgId
        ? userOrganizations.some((org) => org.id === currentOrgId)
        : false;
    const shouldRedirectUser = Boolean(currentOrgId && !userIsMemberOfCurrentOrg);

    React.useEffect(() => {
        if (shouldRedirectUser) {
            toast.error('Error while accessing organization content', null, {
                description: 'You do not have access to this organization.',
            });
        }
    }, [shouldRedirectUser]);

    if (shouldRedirectUser) {
        return (
            <NavigationRedirect
                to={{ screen: { type: 'root', organization: undefined } }}
                replace
            />
        );
    }

    return (
        <CurrentOrganizationProvider organizationId={currentOrgId ?? undefined}>
            <EnforceSSOWrapper organization={organization}>{children}</EnforceSSOWrapper>
        </CurrentOrganizationProvider>
    );
}

/**
 * Render any screen, overlay or main ones.
 */
function ScreenRouter(props: {
    screen: NavigationScreenState;
    isOverlay: boolean;
}): React.ReactElement {
    const { screen, isOverlay } = props;

    // Fallback to start hiding the splashscreen
    // Deeper components in the tree will hide without a delay when they are ready
    useHideSplashscreen();
    useUpdateOrgMemberLastSeenAt();

    const organization = useCurrentOrganizationId();

    switch (screen.type) {
        case 'root':
            return <RootRedirect screen={screen} />;
        case 'org_settings':
            return <OrgSettingsScreenRouter screen={screen} />;
        case 'account':
            return (
                <AppLayout sidebar={<SettingsSidebar screen={screen} />} screen={screen}>
                    <SettingsScreen screen={screen} />
                </AppLayout>
            );
        case 'org_join':
            return <JoinOrganizationScreen screen={screen} />;
        case 'org_invite':
            return <InvitesOrganizationScreen screen={screen} isOverlay={isOverlay} />;
        case 'welcome':
            return <OnboardingFlow screen={screen} />;
        case 'import':
            return <ImportScreen screen={screen} isOverlay={isOverlay} />;
        case 'contentkit_builder':
            return <ContentKitBuilderScreen screen={screen} />;
        case 'org_with_basename':
            return <RedirectOrgWithBasename screen={screen} />;
        case 'join':
            // If there is a user authenticated, but they authenticated using the join screen,
            // redirect to the welcome page so users can get started with setting up an org.
            return <NavigationRedirect to={{ screen: { type: 'root' } }} replace />;
        default:
            return (
                <AppLayout sidebar={<ContentSidebar screen={screen} />} screen={screen}>
                    {organization ? (
                        <OrganizationAccessPlaceholder organization={organization} screen={screen}>
                            <MainScreenSwitch screen={screen} />
                        </OrganizationAccessPlaceholder>
                    ) : (
                        <MainScreenSwitch screen={screen} />
                    )}
                </AppLayout>
            );
    }
}

/**
 * Render an organization settings screen.
 */
export function OrgSettingsScreenRouter(props: { screen: NavigationScreenOrgSettings }) {
    const { screen } = props;
    const { tab, ...rest } = screen;

    if (tab.type === 'billing') {
        const billingScreen = { ...rest, tab };
        return <OrganizationBillingSettings screen={billingScreen} />;
    }

    if (tab.type === 'upgrade') {
        const migrateScreen = { ...rest, tab };
        return <OrganizationMigrateSettings screen={migrateScreen} />;
    }

    const screenWithoutBilling = { ...rest, tab };
    return (
        <AppLayout sidebar={<SettingsSidebar screen={screen} />} screen={screen}>
            <SettingsScreen screen={screenWithoutBilling} />
        </AppLayout>
    );
}

/**
 * Render a navigation screen inside the main UI with the sidebar.
 */
function MainScreenSwitch(props: { screen: NavigationScreenState }): React.ReactElement | null {
    const { screen } = props;

    switch (screen.type) {
        case 'home':
            return <HomeScreen screen={screen} />;
        case 'site':
            return <SiteScreen screen={screen} />;
        case 'sites':
            return <SitesListScreen screen={screen} />;
        case 'space':
            return <SpaceScreen screen={screen} />;
        case 'collection':
            return <CollectionScreen screen={screen} />;
        case 'trash':
            return <TrashScreen screen={screen} />;
        case 'integration':
            return <IntegrationScreen screen={screen} />;
        case 'integrations':
            return <IntegrationsScreen screen={screen} />;
        case 'openapi_specs':
            return <OpenAPISpecsHomeScreen screen={screen} />;
        case 'openapi_spec':
            return <OpenAPISpecScreen screen={screen} />;
        case 'agents':
            return <AgentsScreen screen={screen} />;
        case 'change_requests':
            return <ChangeRequestsScreen screen={screen} />;
        case 'translations':
            return <TranslationsScreen screen={screen} />;
        default:
            return <MainScreenFallback />;
    }
}

function MainScreenFallback() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Page />
            </EmptyStateIcon>
            <EmptyStateNotFoundHeading />
            <Description>The page you are looking for does not exist.</Description>
            <EmptyStateActions>
                <Button href={{ screen: { type: 'home' } }} label="Back to home" kind="secondary" />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * A loading fallback that covers the full screen.
 */
function FullScreenLoadingFallback() {
    return (
        <div className="fixed inset-0 z-10 flex size-full items-center justify-center bg-base">
            <GitBookLoader />
        </div>
    );
}

/**
 * Track feature flags changes in Amplitude.
 */
function TrackFeatureFlags() {
    const client = useClient();

    React.useEffect(() => {
        if (!client) {
            return;
        }

        // Identify the user when the feature flags are updated
        client.on('featuresUpdated', () => {
            const rawFlags = client.getFlags();
            const flags = Object.fromEntries(
                Object.entries(rawFlags).map(([key, value]) => [key, value?.isEnabled])
            ) as EnabledFeatureFlagsMap;
            analytics.identify({ flags });
        });
    }, [client]);

    React.useEffect(() => {
        if (!client) {
            return;
        }

        return withReflag(client);
    }, [client]);

    return null;
}
